// src/context/GuestContext.js - COMPLETE FIX
import React, { createContext, useContext, useReducer, useEffect } from "react";
import { guestAPI } from "../services/api";
import toast from "react-hot-toast";

const GuestContext = createContext();

const initialState = {
  guestCart: [],
  guestItemCount: 0,
  guestSubtotal: 0,
  loading: false,
};

// ✅ Helper function to safely extract product data
const normalizeProduct = (product) => {
  // If product is a string (ID), we can't do much - return null
  if (typeof product === "string") {
    console.error("❌ Product is just an ID string:", product);
    return null;
  }

  // If product is an object, ensure all required fields exist
  if (product && typeof product === "object" && product._id) {
    return {
      _id: product._id,
      name: product.name || "Unknown Product",
      slug: product.slug || "",
      price: Number(product.price) || 0,
      salePrice: product.salePrice ? Number(product.salePrice) : null,
      images: product.images || [],
      brand: product.brand || "",
      category: product.category || null,
      variants: product.variants || [],
    };
  }

  return null;
};

const guestReducer = (state, action) => {
  switch (action.type) {
    case "SET_LOADING":
      return { ...state, loading: action.payload };

    case "SET_GUEST_CART": {
      const cart = action.payload || [];

      // ✅ Filter out any corrupted items
      const validCart = cart.filter((item) => {
        if (!item.product || typeof item.product === "string") {
          console.warn("⚠️ Removing corrupted cart item:", item);
          return false;
        }
        return true;
      });

      const itemCount = validCart.reduce((sum, item) => sum + item.quantity, 0);

      const subtotal = validCart.reduce((sum, item) => {
        const price = Number(
          item.priceAtAdd ?? item.product?.salePrice ?? item.product?.price ?? 0
        );
        return sum + price * item.quantity;
      }, 0);

      console.log("🛒 Guest cart updated:", {
        total: cart.length,
        valid: validCart.length,
        removed: cart.length - validCart.length,
        itemCount,
        subtotal: subtotal.toFixed(2),
      });

      // Save cleaned cart to localStorage
      try {
        localStorage.setItem("guestCart", JSON.stringify(validCart));
      } catch (error) {
        console.error("Failed to save to localStorage:", error);
      }

      return {
        ...state,
        guestCart: validCart,
        guestItemCount: itemCount,
        guestSubtotal: subtotal,
        loading: false,
      };
    }

    case "CLEAR_GUEST_CART":
      localStorage.removeItem("guestCart");
      return {
        ...state,
        guestCart: [],
        guestItemCount: 0,
        guestSubtotal: 0,
      };

    default:
      return state;
  }
};

export const GuestProvider = ({ children }) => {
  const [state, dispatch] = useReducer(guestReducer, initialState);

  // Load and validate cart on mount
  useEffect(() => {
    try {
      const cartData = localStorage.getItem("guestCart");
      console.log("🔄 Loading guest cart from localStorage");

      if (!cartData) {
        console.log("No cart data found");
        return;
      }

      const cart = JSON.parse(cartData);
      console.log("Raw cart data:", cart);

      if (cart.length > 0) {
        // Validate each item
        cart.forEach((item, idx) => {
          console.log(`\nItem ${idx + 1}:`, {
            productType: typeof item.product,
            productId:
              typeof item.product === "string"
                ? item.product
                : item.product?._id,
            hasName: !!item.product?.name,
            priceAtAdd: item.priceAtAdd,
            quantity: item.quantity,
          });
        });

        dispatch({ type: "SET_GUEST_CART", payload: cart });
      }
    } catch (error) {
      console.error("Error loading guest cart:", error);
      localStorage.removeItem("guestCart");
    }
  }, []);

  // Add item to guest cart
  const addToGuestCart = async (product, size, color, quantity = 1) => {
    try {
      // ✅ CRITICAL VALIDATION
      if (!product || typeof product !== "object" || !product._id) {
        console.error("❌ Invalid product data:", {
          received: product,
          type: typeof product,
          hasId: product?._id,
        });
        toast.error("Invalid product data");
        return { success: false, message: "Invalid product" };
      }

      console.log("✅ Valid product received:", {
        id: product._id,
        name: product.name,
        salePrice: product.salePrice,
        price: product.price,
        hasImages: product.images?.length > 0,
      });

      dispatch({ type: "SET_LOADING", payload: true });

      const currentCart = [...state.guestCart];

      // Check if item already exists
      const existingIndex = currentCart.findIndex(
        (item) =>
          item.product._id === product._id &&
          item.variant.size === size &&
          item.variant.color.name === color
      );

      // Find matching variant
      const matchingVariant = Array.isArray(product.variants)
        ? product.variants.find(
            (v) => v.size === size && v.color?.name === color
          )
        : null;

      // Extract price
      const priceToUse = Number(product.salePrice ?? product.price ?? 0);

      if (priceToUse === 0) {
        console.warn("⚠️ Price is 0 for product:", product);
      }

      if (existingIndex > -1) {
        // Update existing item
        currentCart[existingIndex].quantity += quantity;
        console.log(
          "📦 Updated quantity:",
          currentCart[existingIndex].quantity
        );
      } else {
        // ✅ Create new cart item with normalized product data
        const normalizedProduct = normalizeProduct(product);

        if (!normalizedProduct) {
          throw new Error("Failed to normalize product data");
        }

        const newItem = {
          product: normalizedProduct,
          variant: {
            size,
            color: {
              name: color,
              hexCode: matchingVariant?.color?.hexCode || null,
            },
          },
          quantity,
          priceAtAdd: priceToUse,
        };

        console.log("✅ Created new cart item:", {
          productId: newItem.product._id,
          productName: newItem.product.name,
          price: newItem.priceAtAdd,
          quantity: newItem.quantity,
        });

        currentCart.push(newItem);
      }

      dispatch({ type: "SET_GUEST_CART", payload: currentCart });
      toast.success("Item added to cart!");
      return { success: true };
    } catch (error) {
      console.error("Failed to add to guest cart:", error);
      dispatch({ type: "SET_LOADING", payload: false });
      toast.error("Failed to add item to cart");
      return { success: false };
    }
  };

  // Update cart item quantity
  const updateGuestCartItem = async (productId, size, color, quantity) => {
    try {
      dispatch({ type: "SET_LOADING", payload: true });

      const currentCart = state.guestCart.map((item) => {
        if (
          item.product._id === productId &&
          item.variant.size === size &&
          item.variant.color.name === color
        ) {
          return { ...item, quantity };
        }
        return item;
      });

      dispatch({ type: "SET_GUEST_CART", payload: currentCart });
      return { success: true };
    } catch (error) {
      console.error("Failed to update guest cart:", error);
      dispatch({ type: "SET_LOADING", payload: false });
      toast.error("Failed to update item");
      return { success: false };
    }
  };

  // Remove item from guest cart
  const removeFromGuestCart = async (productId, size, color) => {
    try {
      dispatch({ type: "SET_LOADING", payload: true });

      const currentCart = state.guestCart.filter(
        (item) =>
          !(
            item.product._id === productId &&
            item.variant.size === size &&
            item.variant.color.name === color
          )
      );

      dispatch({ type: "SET_GUEST_CART", payload: currentCart });
      toast.success("Item removed");
      return { success: true };
    } catch (error) {
      console.error("Failed to remove from guest cart:", error);
      dispatch({ type: "SET_LOADING", payload: false });
      toast.error("Failed to remove item");
      return { success: false };
    }
  };

  // Clear guest cart
  const clearGuestCart = () => {
    console.log("🧹 Clearing guest cart...");
    dispatch({ type: "CLEAR_GUEST_CART" });
    toast.success("Cart cleared");
  };

  // Convert guest to registered user
  const convertGuestToRegistered = async (email, password) => {
    try {
      const response = await guestAPI.convertGuestToRegistered(email, {
        password,
      });

      if (response.data.success) {
        clearGuestCart();
        toast.success("Account created successfully!");
        return {
          success: true,
          token: response.data.token,
          user: response.data.user,
        };
      }
    } catch (error) {
      console.error("Failed to convert guest:", error);
      const message =
        error.response?.data?.message || "Failed to create account";
      toast.error(message);
      return { success: false, message };
    }
  };

  const value = {
    ...state,
    guestItems: state.guestCart,
    addToGuestCart,
    updateGuestCartItem,
    removeFromGuestCart,
    clearGuestCart,
    convertGuestToRegistered,
  };

  return (
    <GuestContext.Provider value={value}>{children}</GuestContext.Provider>
  );
};

export const useGuest = () => {
  const context = useContext(GuestContext);
  if (!context) {
    throw new Error("useGuest must be used within a GuestProvider");
  }
  return context;
};
